import broxelWechatQr from '../assets/images/broxel-wechat-qr.jpeg'
import pharma4linkWechatQr from '../assets/images/pharma4link-wechat-qr.jpeg'

/**
 * The one place the two offices are defined on the frontend.
 *
 * Mirrored by backend/constants/contactInfo.js — the repo is two packages, not
 * a workspace, so there is no shared module. Change one, change the other.
 *
 * ONE DELIBERATE ASYMMETRY, so nobody "restores" it: since 2026-08-29 an office
 * here carries its OWN email, because /contact prints a card per office. The
 * backend's offices carry none — every address it renders goes into an email
 * footer or the URS PDF letterhead, which speak for the company rather than for
 * one office, so its CONTACT_EMAILS stays the flat company-wide list and has no
 * per-office counterpart to fall out of step with.
 *
 * Machine values (tel:, wa.me, the displayed digits, the QR assets) live here
 * and are NEVER translated: a phone number must be byte-identical in both
 * languages. Only the office name and address prose are i18n keys, because the
 * Arabic page needs an Arabic address. Arabic-Indic digits are produced at
 * render time by localizeDigits, not stored as a second string.
 */
export interface Office {
  id: 'broxel' | 'pharma4link'
  /** i18n key for the office's display name */
  nameKey: string
  /** i18n key for the postal address */
  addressKey: string
  /** E.164, for href="tel:" — never localized */
  tel: string
  /** Grouped for reading; pass through localizeDigits for display */
  telDisplay: string
  /** Full https://wa.me/... URL — never localized */
  whatsapp: string
  /**
   * THIS OFFICE'S OWN address(es) — not every address the company publishes.
   *
   * Until 2026-08-29 both offices carried the full CONTACT_EMAILS list, so each
   * card on /contact printed both addresses. The client asked for one address
   * per card (owner, same day) and it is the more honest shape: a card headed
   * "Broxel Pharmaceuticals" that lists the Pharma4Link address invites a
   * visitor to write to the wrong office, and neither card could then say which
   * address belonged to whom.
   *
   * Still PLURAL: an office may legitimately publish more than one address
   * later, and a scalar would have to be widened at every call site.
   */
  emails: readonly string[]
  wechatQr: string
}

export const BROXEL_OFFICE: Office = {
  id: 'broxel',
  nameKey: 'contact.offices.broxel.name',
  addressKey: 'contact.offices.broxel.address',
  tel: '+201112196777',
  telDisplay: '+20 111 219 6777',
  whatsapp: 'https://wa.me/201112196777',
  emails: ['info@broxel-eg.com'],
  wechatQr: broxelWechatQr,
}

export const PHARMA4LINK_OFFICE: Office = {
  id: 'pharma4link',
  nameKey: 'contact.offices.pharma4link.name',
  addressKey: 'contact.offices.pharma4link.address',
  tel: '+8619515287821',
  telDisplay: '+86 195 1528 7821',
  whatsapp: 'https://wa.me/8619515287821',
  emails: ['info@pharma4link.com'],
  wechatQr: pharma4linkWechatQr,
}

export const OFFICES: readonly Office[] = [BROXEL_OFFICE, PHARMA4LINK_OFFICE]

/**
 * Every address the company publishes, in the order they must be written — for
 * the surfaces that are NOT about one office: the site footer, the homepage CTA
 * band, and the /contact-card page's combined block and its downloadable vCard.
 *
 * `info@pharma4link.com` was added 2026-08-29 on the client's instruction that
 * it be written IN ADDITION to the old one, not in place of it, so neither
 * address retires the other. The established Broxel address stays first because
 * it is the one being added to, which OFFICES order already gives.
 *
 * DERIVED FROM THE OFFICES RATHER THAN LISTED AGAIN, since the same day made
 * the two things different: an address now lives on exactly one office, so a
 * hand-written copy here could fall out of step with them and — the failure
 * that actually matters — a third office's address could be added and printed
 * on its own card while every company-wide surface silently omitted it. Deduped
 * because two offices may one day share an address; a `Set` preserves insertion
 * order, so the sequence is still OFFICES order.
 *
 * This is what a visitor READS. The address that actually RECEIVES platform
 * requests is the backend's ADMIN_EMAIL (set in hPanel, not here), which the
 * same instruction pointed at info@pharma4link.com. The two are deliberately
 * separate: what we publish and where notifications land answer different
 * questions, and ADMIN_EMAIL has never been one of these addresses.
 */
export const CONTACT_EMAILS: readonly string[] = [
  ...new Set(OFFICES.flatMap((office) => office.emails)),
]
